import React, { useEffect } from 'react'
import { Provider, useSelector } from 'react-redux'
import {
  Router,
  Switch,
  Route,
  BrowserRouter,
  Redirect,
} from 'react-router-dom'
import AppContext from './contexts/AppContext'
import history from 'history.js'
import routes from './RootRoutes'
import { Store } from './redux/Store'
import { AuthProvider } from 'app/contexts/JWTAuthContext'
import { SettingsProvider } from 'app/contexts/SettingsContext'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './custom.scss'
import './extensions'
import {
  hasCapability,
  isAuthenticated,
  isUserInRole,
  wrapLayout,
} from './auth/utils'
import GlobalModal from './reusable/GlobalModal'
import { useReloadSW } from './ducks/serviceWorker/hooks'
import MatxLoading from './components/MatxLoading/MatxLoading'
import GlobalCss from './styles/GlobalCss'
import MatxSuspense from './components/MatxSuspense/MatxSuspense'
import MatxTheme from './components/MatxTheme/MatxTheme'
import InstallationModal from './components/InstallationModal/InstallationModal'
import NoPasswordModal from './components/NoPasswordModal/NoPasswordModal'
import TOTPModal from './components/TOTPModal/TOTPModal'
import useIsMobile from './hooks/useIsMobile'
// import TotemPrintModal from './components/TotemPrintModal/TotemPrintModal'
import { getUserInfo } from './ducks/auth/actions'
import Firebase from 'Firebase'
import Analytics from './reusable/Analytics'
import 'moment/locale/pt-br'
import { useFeatureFlags, useUserInfo } from './ducks/auth/hooks'
import {
  areSettled as areFeatureFlagsSettled,
  getFeatureFlag,
} from './ducks/featureFlags/selectors'
// import InAppMessagesModal from './components/InAppMessagesModal/InAppMessagesModal'
import SurveyPromptModal from './components/SurveyPromptModal/SurveyPromptModal'
import InternalCampaignPopup from './components/InternalCampaigns/InternalCampaignPopup'

const Offline = React.lazy(() => import('./views/sessions/Offline'))

const AppRoute = ({
  path,
  exact,
  component,
  roles,
  capability,
  isPublic,
  userCondition,
  featureFlag,
  hideLayout,
}) => {
  const userInfo = useUserInfo()
  const featureFlags = useFeatureFlags()
  const flagsSettled = useSelector(areFeatureFlagsSettled)
  const isFeatureFlagOn = useSelector(
    (state) => !featureFlag || getFeatureFlag(state, featureFlag)
  )

  // When `capability` is present, route access is gated by the new
  // permission-mode-aware capability check. `roles` is fed in as the
  // legacy fallback, so existing user_type-based users keep working
  // until they're individually graduated to `permission_mode=django`.
  // When `capability` is absent we keep the legacy behaviour intact.
  const isUserNotAllowed = capability
    ? !hasCapability(capability, { legacyRoles: roles })
    : roles && !isUserInRole(roles)
  const isUserAllowedByCondition =
    !userCondition || userCondition(userInfo, featureFlags)

  if (!isPublic && !isAuthenticated()) return <Redirect to="/session/signin" />
  // A flag-gated route reached before the flags are known (first login on
  // this browser, while the fetch is in flight) waits instead of bouncing to
  // 404: "off" and "not loaded yet" both read `false`.
  if (featureFlag && !isFeatureFlagOn && !flagsSettled) return <MatxLoading />
  if (!isFeatureFlagOn || isUserNotAllowed || !isUserAllowedByCondition)
    return <Redirect to="/session/404" />

  return (
    <Route
      path={path}
      component={isPublic || hideLayout ? component : wrapLayout(component)}
      exact={exact}
    />
  )
}

const ServiceWorkerUpdateHandler = () => {
  const realoadSw = useReloadSW()
  const { updated: serviceWorkerUpdated } = useSelector(
    ({ serviceWorker }) => serviceWorker
  )

  useEffect(() => {
    if (serviceWorkerUpdated)
      toast.info(
        'Uma nova atualização do seu aplicativo está disponível, clique aqui para iniciar!',
        {
          toastId: 'sw-update',
          autoClose: false,
          onClick: realoadSw,
        }
      )
  }, [serviceWorkerUpdated, realoadSw])

  return null
}

const AppWraper = ({ children }) => {
  return (
    <AppContext.Provider value={{ routes: [] }}>
      <Provider store={Store}>
        <ServiceWorkerUpdateHandler />
        <SettingsProvider>
          <MatxTheme>
            {children}
            <GlobalCss />
          </MatxTheme>
        </SettingsProvider>
      </Provider>
    </AppContext.Provider>
  )
}

const App = () => {
  const isMobile = useIsMobile()
  const isOnline = window.navigator.onLine

  // A resumed session (token already in the store) refreshes the feature
  // flags through getUserInfo -> fetchFeatureFlags. Until that returns, the
  // UI renders with the last flags persisted in localStorage (see
  // ducks/featureFlags/reducers.js), not with every flag off.
  useEffect(() => {
    const state = Store.getState()
    if (state?.auth?.token) Store.dispatch(getUserInfo(state.auth.token))
    Firebase.init()
    Analytics.init()
  })

  if (!isOnline)
    return (
      <AppWraper>
        <MatxSuspense>
          <Offline />
        </MatxSuspense>
      </AppWraper>
    )

  return (
    <AppWraper>
      <ToastContainer
        position={isMobile ? 'bottom-center' : 'top-right'}
        theme="colored"
      />
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        <Router history={history}>
          <GlobalModal />
          <AuthProvider>
            <MatxSuspense>
              <Switch>
                {routes.map((routeProps, i) => (
                  <AppRoute key={i} {...routeProps} />
                ))}
              </Switch>
            </MatxSuspense>
          </AuthProvider>
        </Router>
      </BrowserRouter>
      <InstallationModal />
      {/* <TotemPrintModal /> */}
      <NoPasswordModal />
      <TOTPModal />
      {/* <InAppMessagesModal /> */}
      <SurveyPromptModal />
      <InternalCampaignPopup />
    </AppWraper>
  )
}

export default App
